import { type Driver, type DriverSpreadsheetResult, GraphQLDriversRequester, type IDriverSpreadsheetRowAPI, type NewDriver } from '@cobliteam/api-lib';
import type { UUID } from '@cobliteam/common-types';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, type UseSuspenseQueryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { trimDriverName, trimDriverNameFromSpreadsheet } from './utils';

export const QUERY_KEY = 'DRIVERS';
export const QUERY_KEY_WITH_VEHICLES = 'DRIVERS_WITH_VEHICLES';

type GetDriversParams = { options?: Partial<UseQueryOptions<Driver[]>> } | undefined;

const useDriver = () => {
  const graphqlDriverRequester = new GraphQLDriversRequester();

  const queryClient = useQueryClient();

  const getDrivers = (params?: GetDriversParams) => {
    return useQuery<Driver[]>({
      ...params?.options,
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const getDriversSuspense = (options?: Partial<UseSuspenseQueryOptions<Driver[]>>) => {
    return useSuspenseQuery<Driver[]>({
      ...options,
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
      retry: options?.retry ?? 0,
      refetchOnMount: options?.refetchOnMount ?? true,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
      staleTime: options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const getDriversWithVehicles = (params?: GetDriversParams) => {
    return useQuery<Driver[]>({
      ...params?.options,
      queryKey: [QUERY_KEY_WITH_VEHICLES],
      queryFn: () => graphqlDriverRequester.getDriversWithVehicles(),
      retry: params?.options?.retry ?? 0,
      enabled: params?.options?.enabled ?? true,
      refetchOnMount: params?.options?.refetchOnMount ?? true,
      refetchOnWindowFocus: params?.options?.refetchOnWindowFocus ?? true,
      staleTime: params?.options?.staleTime ?? Number.POSITIVE_INFINITY,
    });
  };

  const prefetchDrivers = () => {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY],
      queryFn: () => graphqlDriverRequester.getDrivers(),
    });
  };

  const prefetchDriversWithVehicles = () => {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY_WITH_VEHICLES],
      queryFn: () => graphqlDriverRequester.getDriversWithVehicles(),
    });
  };

  const useSaveDriver = () => {
    return useMutation<Driver, unknown, Driver>({
      mutationKey: [QUERY_KEY],
      mutationFn: async (driver) => graphqlDriverRequester.saveDriver(trimDriverName(driver)),
      onMutate: async (driver) => {
        await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
        const previousDrivers = queryClient.getQueryData<Driver[]>([QUERY_KEY]);
        queryClient.setQueryData<Driver[]>([QUERY_KEY], (oldDrivers) => {
          if (oldDrivers) return oldDrivers.map((d) => (d.id === driver.id ? driver : d));
        });
        return { previousDrivers };
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        Alert.success('Motorista salvo com sucesso.');
      },
      onError: (err: any, variables, context: any) => {
        queryClient.setQueryData([QUERY_KEY], context.previousDrivers);
        Alert.error('Falha ao salvar motorista. Tente novamente mais tarde.');
      },
    });
  };

  const useSaveDrivers = () => {
    return useMutation<Driver[], unknown, NewDriver[]>({
      mutationFn: async (drivers) => graphqlDriverRequester.saveDrivers(drivers.map((d) => trimDriverName(d))),
      onSuccess: () => {
        Alert.success('Motoristas salvos com sucesso.');
      },
      onError: () => {
        Alert.error('Falha ao salvar motoristas. Tente novamente mais tarde.');
      },
    });
  };

  const useDriversImport = () => {
    return useMutation<DriverSpreadsheetResult, unknown, IDriverSpreadsheetRowAPI[]>({
      mutationFn: async (drivers) => graphqlDriverRequester.driversImport(drivers.map((d) => trimDriverNameFromSpreadsheet(d))),
      onError: () => {
        Alert.error('Falha ao Importar motoristas. Tente novamente mais tarde.');
      },
    });
  };

  const useDeleteDriver = () => {
    return useMutation<UUID, unknown, UUID>({
      mutationFn: async (id) => graphqlDriverRequester.deleteDriver(id),
      onSuccess: () => {
        Alert.success('Motorista deletado com sucesso.');
        queryClient.resetQueries({ queryKey: [QUERY_KEY] });
        queryClient.resetQueries({ queryKey: ['QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY'] });
        queryClient.resetQueries({ queryKey: ['IDENTIFICATION_TOKEN_V2'] });
      },
      onError: () => {
        Alert.error('Falha ao deletar motorista. Tente novamente mais tarde.');
      },
    });
  };

  return {
    getDrivers,
    getDriversSuspense,
    getDriversWithVehicles,
    useSaveDriver,
    useSaveDrivers,
    useDriversImport,
    useDeleteDriver,
    prefetchDrivers,
    prefetchDriversWithVehicles,
  };
};

export default useDriver;
